import { jwtVerify, importSPKI, decodeJwt } from 'jose';
import { JWTPayload } from '../types';
import { isDemoMode } from './demoDataService';

export class JWTService {
  private static publicKey: CryptoKey | null = null;
  private static isInitialized: boolean = false;
  private static initializationPromise: Promise<void> | null = null;

  /**
   * Initialize the JWT service with RSA public key
   * Call this once at app startup - safe to call multiple times
   */
  static async initialize(): Promise<void> {
    // If already initialized, return immediately
    if (this.isInitialized) {
      return;
    }

    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = this.doInitialize();
    return this.initializationPromise;
  }

  private static async doInitialize(): Promise<void> {
    const publicKeyPem = process.env.REACT_APP_JWT_PUBLIC_KEY;
    
    if (!publicKeyPem) {
      console.warn('⚠️ No JWT public key configured - JWT verification disabled');
      this.isInitialized = true;
      return;
    }

    try {
      // Import RSA public key for verification.
      // Netlify env flows (and .env files) can store multi-line PEMs with literal
      // "\n" sequences — normalize them, same as netlify/functions/auth-exchange.js.
      this.publicKey = await importSPKI(publicKeyPem.replace(/\\n/g, '\n'), 'RS256');
      this.isInitialized = true;
      
      // Only log in development
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ JWT service initialized with RSA public key');
      }
    } catch (error) {
      console.error('❌ Failed to initialize JWT service:', error);
      this.isInitialized = true; // Mark as initialized even if failed
      throw new Error('Invalid JWT public key configuration');
    }
  }

  /**
   * Verify and decode JWT token with RSA signature verification
   * For production embedding in third-party apps
   */
  static async verifyToken(token: string): Promise<JWTPayload | null> {
    try {
      // Remove 'Bearer ' prefix if present
      const cleanToken = token.replace(/^Bearer\s+/, '');
      
      if (!this.publicKey) {
        console.warn('⚠️ JWT verification skipped - no public key configured');
        return this.decodeTokenUnsafe(cleanToken);
      }

      // Verify JWT signature with RSA public key
      const { payload } = await jwtVerify(cleanToken, this.publicKey, {
        algorithms: ['RS256']
      });

      const jwtPayload = payload as JWTPayload;

      // Validate required fields for authentication (externalId is optional - only needed for CareDaily API)
      const requiredFields = ['residentId', 'facilityId', 'iat', 'exp'];
      const missingFields = requiredFields.filter(field => !jwtPayload[field as keyof JWTPayload]);
      
      if (missingFields.length > 0) {
        console.error('❌ JWT missing required fields:', missingFields);
        return null;
      }

      // Warn if externalId is missing (needed for CareDaily API data)
      if (!jwtPayload.externalId) {
        console.warn('⚠️ JWT missing externalId - CareDaily API data may not be available');
      }

      // Default role to 'resident' if not provided
      if (!jwtPayload.role) {
        jwtPayload.role = 'resident';
      }

      // Only log JWT verification in development (sensitive info)
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ JWT verified successfully for resident:', jwtPayload.residentId, 'role:', jwtPayload.role);
      }
      return jwtPayload;

    } catch (error) {
      console.error('❌ JWT verification failed:', error);
      return null;
    }
  }

  /**
   * Decode JWT without verification (for development/testing)
   * Note: This should NOT be used in production embedding
   */
  static decodeTokenUnsafe(token: string): JWTPayload | null {
    try {
      // Remove 'Bearer ' prefix if present
      const cleanToken = token.replace(/^Bearer\s+/, '');
      
      // Decode without verification
      const decoded = decodeJwt(cleanToken) as JWTPayload;
      
      if (!decoded) {
        console.error('❌ Failed to decode JWT token');
        return null;
      }

      // For embedding, we need all required fields including timestamps
      const requiredFields = ['residentId', 'facilityId', 'externalId', 'iat', 'exp'];
      const missingFields = requiredFields.filter(field => !decoded[field as keyof JWTPayload]);
      
      if (missingFields.length > 0) {
        console.error('❌ JWT missing required fields:', missingFields);
        return null;
      }

      // Default role to 'resident' if not provided
      if (!decoded.role) {
        decoded.role = 'resident';
      }

      console.log('⚠️ JWT decoded WITHOUT verification for resident:', decoded.residentId, 'role:', decoded.role);
      return decoded;

    } catch (error) {
      console.error('❌ Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Main method to process JWT token
   * Uses verification in production, unsafe decode in development
   */
  static async processToken(token: string): Promise<JWTPayload | null> {
    // Demo mode: return demo payload
    if (isDemoMode()) {
      console.log('🎭 Demo Mode: Using demo resident data');
      return {
        residentId: 'DEMO-001',
        facilityId: 'demo_facility',
        externalId: 'demo_external_001',
        role: 'resident',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 86400 // 24 hours
      };
    }

    // In development or testing, allow bypass
    if (process.env.REACT_APP_BYPASS_JWT === 'true') {
      console.log('🧪 JWT bypass enabled - using test data');
      const testRole = process.env.REACT_APP_TEST_ROLE as 'STAFF' | 'resident' || 'resident';
      return {
        residentId: process.env.REACT_APP_TEST_RESIDENT_LOCATION_ID || '287743',
        facilityId: process.env.REACT_APP_TEST_FACILITY_ID || 'facility_123',
        externalId: 'test_external_id_456',
        role: testRole,
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 3600
      };
    }

    // Production: verify with RSA signature
    return await this.verifyToken(token);
  }

  /**
   * Extract JWT from URL query parameters
   */
  static getTokenFromQuery(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }

  /**
   * Extract JWT from Authorization header (for iframe embedding)
   */
  static getTokenFromHeader(): string | null {
    // This would typically be passed via postMessage from parent frame
    // For now, we'll check for a global variable set by the parent
    return (window as any).authToken || null;
  }

  /**
   * Get JWT from various sources (query param, header, localStorage)
   */
  static getToken(): string | null {
    // Try query parameter first
    let token = this.getTokenFromQuery();
    
    if (!token) {
      // Try from header/global variable
      token = this.getTokenFromHeader();
    }
    
    if (!token) {
      // Try from localStorage as fallback
      token = localStorage.getItem('caredaily_token');
    }
    
    return token;
  }

  /**
   * Store token in localStorage for persistence
   */
  static storeToken(token: string): void {
    localStorage.setItem('caredaily_token', token);
  }

  /**
   * Clear stored token
   */
  static clearToken(): void {
    localStorage.removeItem('caredaily_token');
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(payload: JWTPayload): boolean {
    if (!payload.exp) return false;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }
}
